import { useState, useEffect } from "react"
import { getorgInfo } from "@/api/kuizy"
import { BookText } from "lucide-react"

const EMPTY = {
  organization_name: "",
  tax_id: "",
  contact_name: "",
  email: "",
  phone: "",
  addr1: "",
  addr2: "",
  sub_district: "",
  district: "",
  province: "",
  postal_code: "",
}

export default function Step1BillingForm({ plan, billing, onChange, onNext, onBack }) {
  const [orgInfo, setOrgInfo] = useState(null)
  const [useOrg, setUseOrg] = useState(false)

  useEffect(() => {
    getorgInfo().then(r => setOrgInfo(r.data || null)).catch(() => { })
  }, [])

  const set = (k, v) => onChange({ ...billing, [k]: v })

  const handleUseOrg = (checked) => {
    setUseOrg(checked)
    if (checked && orgInfo) {
      onChange({
        organization_name: orgInfo.name_th || orgInfo.name_en || "",
        tax_id: orgInfo.tax_id || "",
        contact_name: "",
        email: orgInfo.email || "",
        phone: orgInfo.phone || "",
        addr1: orgInfo.addr1 || "",
        addr2: orgInfo.addr2 || "",
        sub_district: orgInfo.sub_district || "",
        district: orgInfo.district || "",
        province: orgInfo.province || "",
        postal_code: orgInfo.postal_code || "",
      })
    } else if (!checked) {
      onChange(EMPTY)
    }
  }

  const vat = Math.round((plan.price_monthly || 0) * 0.07)
  const total = (plan.price_monthly || 0) + vat

  const required = "border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 w-full"
  const label = "block text-sm font-medium text-gray-700 mb-1"

  const canNext = billing.organization_name && billing.tax_id && billing.contact_name &&
    billing.email && billing.phone && billing.addr1 && billing.sub_district &&
    billing.district && billing.province && billing.postal_code

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
      {/* Left: Form */}
      <div className="flex-1 w-full min-w-0">
        <h2 className="text-xl font-semibold mb-1">ข้อมูลสำหรับออกใบกำกับภาษี</h2>
        <p className="text-sm text-gray-500 mb-5">กรอกข้อมูลที่ถูกต้องและที่อยู่ใช้สำหรับออกใบกำกับภาษีและใบเสร็จรับเงิน</p>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm font-semibold mb-4 flex items-center gap-2">
            <span className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center text-xs"><BookText className="text-pink-600" /></span>
            ที่อยู่วางบิล / ใบเสร็จ
          </p>

          <label className="flex items-center gap-2 text-sm text-gray-600 mb-5 cursor-pointer">
            <input
              type="checkbox"
              checked={useOrg}
              onChange={e => handleUseOrg(e.target.checked)}
              className="accent-pink-500 w-4 h-4"
            />
            ใช้ที่อยู่เดียวกับที่อยู่องค์กร
          </label>

          <div className="grid grid-cols-1 gap-4">
            {/* Organization name */}
            <div>
              <label className={label}>ชื่อองค์กรบนใบวางบิล <span className="text-pink-500">*</span></label>
              <input
                value={billing.organization_name}
                onChange={e => set("organization_name", e.target.value)}
                placeholder="ชื่อบริษัท / ห้างหุ้นส่วน"
                className={required}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={label}>เลขผู้เสียภาษี <span className="text-pink-500">*</span></label>
                <input
                  value={billing.tax_id}
                  onChange={e => set("tax_id", e.target.value)}
                  placeholder="0000000000000"
                  className={required}
                />
              </div>
              <div>
                <label className={label}>ผู้ติดต่อ <span className="text-pink-500">*</span></label>
                <input
                  value={billing.contact_name}
                  onChange={e => set("contact_name", e.target.value)}
                  placeholder="ชื่อ-นามสกุล"
                  className={required}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={label}>อีเมล <span className="text-pink-500">*</span></label>
                <input
                  type="email"
                  value={billing.email}
                  onChange={e => set("email", e.target.value)}
                  placeholder="accounting@company.com"
                  className={required}
                />
              </div>
              <div>
                <label className={label}>โทรศัพท์ <span className="text-pink-500">*</span></label>
                <input
                  value={billing.phone}
                  onChange={e => set("phone", e.target.value)}
                  placeholder="02-000-0000"
                  className={required}
                />
              </div>
            </div>

            <div>
              <label className={label}>ที่อยู่บรรทัดที่ 1 <span className="text-pink-500">*</span></label>
              <input
                value={billing.addr1}
                onChange={e => set("addr1", e.target.value)}
                placeholder="เลขที่ อาคาร ซอย ถนน"
                className={required}
              />
            </div>
            <div>
              <label className={label}>ที่อยู่บรรทัดที่ 2</label>
              <input
                value={billing.addr2}
                onChange={e => set("addr2", e.target.value)}
                placeholder="ชั้น ห้อง หรือ อาคาร (ถ้ามี)"
                className={required}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={label}>ตำบล / แขวง <span className="text-pink-500">*</span></label>
                <input
                  value={billing.sub_district}
                  onChange={e => set("sub_district", e.target.value)}
                  placeholder="ตำบล / แขวง"
                  className={required}
                />
              </div>
              <div>
                <label className={label}>อำเภอ / เขต <span className="text-pink-500">*</span></label>
                <input
                  value={billing.district}
                  onChange={e => set("district", e.target.value)}
                  placeholder="อำเภอ / เขต"
                  className={required}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={label}>จังหวัด <span className="text-pink-500">*</span></label>
                <input
                  value={billing.province}
                  onChange={e => set("province", e.target.value)}
                  placeholder="จังหวัด"
                  className={required}
                />
              </div>
              <div>
                <label className={label}>รหัสไปรษณีย์ <span className="text-pink-500">*</span></label>
                <input
                  value={billing.postal_code}
                  onChange={e => set("postal_code", e.target.value)}
                  placeholder="10000"
                  className={required}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Summary */}
      <PlanSummary plan={plan} vat={vat} total={total} billing={billing} step={1} />
    </div>
  )
}

export function PlanSummary({ plan, vat, total, billing, step }) {

  const formatThaiDate = (date) => {
    return date.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const today = formatThaiDate(new Date());

  const features = [
    { label: "Admins", val: plan.max_admins },
    { label: "Creators", val: plan.max_creators },
    { label: "Learners", val: plan.max_learners },
    { label: "Courses", val: plan.max_courses },
    { label: "Exams", val: plan.max_exams },
    { label: "Questions", val: plan.max_questions },
  ]

  return (
    <div className="w-full lg:w-80 shrink-0">
      <div className="rounded-xl overflow-hidden border border-pink-200 shadow-sm">

        {/* HEADER */}
        <div className="bg-pink-100 px-5 py-4">
          <p className="font-semibold text-sm text-gray-800">
            {plan.display_name}
          </p>
          <p className="text-xs text-pink-600 mt-1">
            รายเดือน · เริ่ม {today}
          </p>
        </div>

        {/* BODY */}
        <div className="bg-white py-4">

          {/* PRICE SECTION */}
          <div className="space-y-3 text-sm pb-4 mb-4 px-5">
            <div className="flex justify-between">
              <span className="text-gray-500">ราคาแผน</span>
              <span className="font-medium text-gray-800">
                ฿{(plan.price_monthly || 0).toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between border-b border-gray-200 pb-4">
              <span className="text-gray-500">VAT 7%</span>
              <span className="font-medium text-gray-800">
                ฿{vat.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">ส่วนลด</span>
              <span className="text-green-500 font-medium">
                − ฿0
              </span>
            </div>
          </div>

          {/* TOTAL */}
          <div className="flex justify-between items-center mb-5 bg-gray-50 p-4">
            <span className="font-semibold text-sm text-gray-700">
              ยอดรวมทั้งหมด
            </span>
            <span className="text-pink-500 font-bold text-xl">
              ฿{total.toLocaleString()}
            </span>
          </div>

          {/* FEATURES */}
          <div className="text-xs text-gray-600 space-y-2 px-5">
            <p className="font-medium text-gray-400 mb-1">สิทธิ์ที่ได้รับ</p>

            {features.map(f => (
              <div key={f.label} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 mt-1 rounded-full bg-green-400 shrink-0" />
                <span>
                  {f.label}:{" "}
                  {f.val === -1
                    ? "ไม่จำกัด"
                    : `${f.val?.toLocaleString()} ${f.label === "Admins" ||
                      f.label === "Creators" ||
                      f.label === "Learners"
                      ? "คน"
                      : f.label === "Questions"
                        ? "คำถาม"
                        : "หลักสูตร"
                    }`}
                </span>
              </div>
            ))}
          </div>

          {/* BILLING */}
          {step >= 2 && billing && (
            <div className="mt-5 pt-4 border-t border-gray-200 text-xs text-gray-600 space-y-1 px-4">
              <p className="font-medium text-gray-700 mb-1">ข้อมูลบิล</p>
              {billing.organization_name && <p>• {billing.organization_name}</p>}
              {billing.tax_id && <p>• เลขผู้เสียภาษี: {billing.tax_id}</p>}
              {billing.email && <p>• {billing.email}</p>}
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
